import React, { useState, useEffect, useCallback, useRef } from "react";
import { ClientNavSidebar } from "../components/editor/ClientNavSidebar";
import { useParams, useSearchParams, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Globe, CreditCard, ExternalLink, Loader2, CheckCircle2,
  Lock, ShoppingCart, ChevronDown, ChevronUp, Search, RefreshCw,
  AlertCircle, Gift, Check, X, Sparkles, Bot, Wand2,
  Code2, Plus, Trash2, Timer, Image, LogOut,
  Clock, HelpCircle, Copy, Zap, Users, DollarSign, BarChart2,
} from "lucide-react";
import { EditTab } from "./ClientDashboard.EditTab";
import { useCountdown } from "./ClientDashboard.Controls";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const MOD_PACKS = [
  { count: 10, usd: 10, uyu: 400 },
  { count: 20, usd: 20, uyu: 750 },
  { count: 30, usd: 30, uyu: 1100 },
  { count: 50, usd: 50, uyu: 1800 },
];



function DomainAccordion({ id, openId, onToggle, icon: Icon, title, badge, badgeColor = "bg-[#FF4F00]", children }) {
  const isOpen = openId === id;
  return (
    <div className={`border rounded-2xl overflow-hidden transition-all duration-200 ${isOpen ? "border-[#0074b7]/40 bg-[#0074b7]/[0.03]" : "border-gray-200 bg-white"}`}>
      <button className="w-full flex items-center justify-between px-4 py-4 hover:bg-gray-50/50" onClick={() => onToggle(id)}>
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center transition-colors ${isOpen ? "bg-[#0074b7]/10" : "bg-gray-100"}`}>
            <Icon size={17} className={isOpen ? "text-[#0074b7]" : "text-gray-500"} />
          </div>
          <span className={`text-sm font-bold transition-colors ${isOpen ? "text-[#0074b7]" : "text-[#0A0A0A]"}`}>{title}</span>
          {badge && <span className={`text-[10px] font-black ${badgeColor} text-white px-2 py-0.5 rounded-full`}>{badge}</span>}
        </div>
        {isOpen ? <ChevronUp size={16} className="text-[#0074b7]" /> : <ChevronDown size={16} className="text-gray-400" />}
      </button>
      {isOpen && <div className="px-4 pb-5 border-t border-gray-100 pt-4">{children}</div>}
    </div>
  );
}

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-100 border-b border-gray-200">
        <span className="text-[10px] font-black text-gray-500 uppercase tracking-wider">{label}</span>
        <button onClick={copy} className="flex items-center gap-1 text-[10px] font-bold text-[#0074b7] hover:text-blue-800">
          {copied ? <><Check size={10} /> Copiado</> : <><Copy size={10} /> Copiar</>}
        </button>
      </div>
      <p className="px-3 py-2 text-xs font-mono text-gray-700 break-all">{value}</p>
    </div>
  );
}

function DomainContactForm({ state, onChange, hasPreFilled = false }) {
  return (
    <div className="space-y-2">
      {hasPreFilled && (
        <div className="flex items-center gap-1.5 text-[10px] text-green-700 font-semibold bg-green-50 border border-green-200 rounded-lg px-2.5 py-1.5">
          <Check size={11} className="flex-shrink-0" />
          Datos pre-llenados desde tu cuenta. Revisá y completá si falta algo.
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        {[["first_name","Nombre"],["last_name","Apellido"],["email","Email"],["phone","Teléfono"],["address1","Dirección"],["city","Ciudad"]].map(([k,l]) => (
          <input key={k} placeholder={l} value={state[k] || ""} onChange={e => onChange({...state, [k]: e.target.value})}
            className="border border-gray-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:border-[#0074b7]" />
        ))}
      </div>
    </div>
  );
}

// ─── Site Analytics Tab ───────────────────────────────────────────────────────
function SiteAnalyticsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/client/${token}/site-analytics`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const daily = data?.daily || [];
  const maxCount = daily.length ? Math.max(1, ...daily.map(d => d.count)) : 1;

  // Compute today & yesterday
  const todayStr = new Date().toISOString().split("T")[0];
  const yesterdayStr = new Date(Date.now() - 86400000).toISOString().split("T")[0];
  const today = daily.find(d => d.date === todayStr)?.count ?? 0;
  const yesterday = daily.find(d => d.date === yesterdayStr)?.count ?? 0;
  const weekTotal = data?.this_week ?? 0;
  const prevWeekTotal = data?.prev_week ?? 0;
  const monthTotal = data?.this_month ?? 0;
  const total = data?.total ?? 0;

  const trend = (curr, prev) => {
    if (prev === 0) return curr > 0 ? "up" : "flat";
    return curr >= prev ? "up" : "down";
  };

  const KPICard = ({ label, value, sub, subLabel, t }) => {
    const colors = { up: "text-green-600 bg-green-50", down: "text-red-500 bg-red-50", flat: "text-gray-400 bg-gray-50" };
    const arrows = { up: "↑", down: "↓", flat: "—" };
    return (
      <div className="bg-white border border-gray-100 rounded-2xl p-4 flex flex-col gap-1 shadow-sm">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</span>
        <span className="text-2xl font-black text-[#0A0A0A]" data-testid={`visits-${label.toLowerCase().replace(/\s+/g,"-")}`}>{value}</span>
        {sub !== undefined && (
          <div className={`inline-flex items-center gap-1 self-start px-1.5 py-0.5 rounded-lg text-[10px] font-bold ${colors[t]}`}>
            {arrows[t]} {sub} {subLabel}
          </div>
        )}
      </div>
    );
  };

  // Last 14 days only for chart (cleaner)
  const chartData = daily.slice(-14);
  const chartMax = Math.max(1, ...chartData.map(d => d.count));

  return (
    <div className="px-4 pt-4 pb-24 max-w-lg mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-black text-[#0A0A0A] tracking-tight">Analíticas del sitio</h2>
        <span className="text-[10px] text-gray-400 font-semibold">Últimos 30 días</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-gray-300" size={28} />
        </div>
      ) : (
        <>
          {/* KPI grid — 2×2 */}
          <div className="grid grid-cols-2 gap-3">
            <KPICard label="Hoy" value={today} sub={yesterday} subLabel="ayer" t={trend(today, yesterday)} />
            <KPICard label="Esta semana" value={weekTotal} sub={prevWeekTotal} subLabel="semana ant." t={trend(weekTotal, prevWeekTotal)} />
            <KPICard label="Este mes" value={monthTotal} />
            <KPICard label="Total histórico" value={total} />
          </div>

          {/* Daily bar chart — last 14 days */}
          <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">Visitas — últimos 14 días</p>
            {chartData.some(d => d.count > 0) ? (
              <div className="space-y-2">
                <div className="flex items-end gap-1" style={{ height: 96 }}>
                  {chartData.map((d) => {
                    const h = Math.max(3, Math.round((d.count / chartMax) * 96));
                    const isToday = d.date === todayStr;
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center justify-end group gap-0.5" title={`${d.date}: ${d.count}`}>
                        <span className="hidden group-hover:flex text-[9px] text-gray-500 font-bold">{d.count}</span>
                        <div
                          className={`w-full rounded-t-md transition-all ${isToday ? "bg-[#ffc700]" : "bg-[#0A0A0A]/70 group-hover:bg-[#ffc700]/80"}`}
                          style={{ height: `${h}px` }}
                        />
                      </div>
                    );
                  })}
                </div>
                {/* X-axis labels: show day/month for first and last */}
                <div className="flex items-start gap-1">
                  {chartData.map((d, i) => {
                    const show = i === 0 || i === chartData.length - 1 || i === Math.floor(chartData.length / 2);
                    const [,mm,dd] = d.date.split("-");
                    return (
                      <div key={d.date} className="flex-1 text-center">
                        <span className={`text-[8px] text-gray-400 font-medium ${show ? "opacity-100" : "opacity-0"}`}>{dd}/{mm}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="py-10 text-center">
                <BarChart2 size={28} className="text-gray-200 mx-auto mb-2" />
                <p className="text-xs font-semibold text-gray-400">Sin visitas registradas aún</p>
                <p className="text-[11px] text-gray-300 mt-1">Compartí tu sitio para empezar a ver estadísticas</p>
              </div>
            )}
          </div>

          {/* Insight card */}
          {weekTotal > 0 && (
            <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl px-4 py-3 flex items-start gap-3">
              <span className="text-[#ffc700] mt-0.5">★</span>
              <div>
                <p className="text-xs font-bold text-white">
                  {weekTotal > prevWeekTotal && prevWeekTotal > 0
                    ? `+${weekTotal - prevWeekTotal} visitas más que la semana pasada`
                    : weekTotal > 0 ? `${weekTotal} visitas esta semana` : "Sin visitas esta semana"}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {today > 0 ? `${today} visita${today > 1 ? "s" : ""} hoy` : "Ninguna visita hoy todavía"}
                </p>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-2xl px-4 py-3">
            <p className="text-xs text-blue-700 font-semibold">Las visitas se registran cuando alguien accede a tu sitio desde internet (no desde este panel).</p>
          </div>
        </>
      )}
    </div>
  );
}


// ─── Domain Tab ───────────────────────────────────────────────────────────────
function DomainTab({ token, domainData, onRefresh }) {
  const [open, setOpen] = useState(null);
  const [ownDomain, setOwnDomain] = useState(domainData?.custom_domain || "");
  const [ownLoading, setOwnLoading] = useState(false);
  const [ownResult, setOwnResult] = useState(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [searchKw, setSearchKw] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", phone: "+598", country: "UY", city: "", address1: "", state_province: "Montevideo", postal_code: "11000" });
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [freeForm, setFreeForm] = useState({ domain_name: "", first_name: "", last_name: "", email: "", phone: "+598", country: "UY", city: "", address1: "", state_province: "Montevideo", postal_code: "11000" });
  const [freeLoading, setFreeLoading] = useState(false);
  // CF state
  const [cfStatus, setCfStatus] = useState(null);
  const [cfDomainLoading, setCfDomainLoading] = useState(false);

  // Pre-fill contact forms with existing account data
  useEffect(() => {
    const ci = domainData?.contact_info;
    if (!ci) return;
    const patch = {
      first_name: ci.first_name || "",
      last_name: ci.last_name || "",
      email: ci.email || "",
      phone: ci.phone || "+598",
    };
    setContact(c => ({ ...c, ...patch }));
    setFreeForm(f => ({ ...f, ...patch }));
  }, [domainData]);

  useEffect(() => {
    fetch(`${API}/client/${token}/deploy/status`)
      .then(r => r.json())
      .then(d => setCfStatus(d))
      .catch(() => {});
  }, [token]);

  const setDomainAction = async () => {
    setOwnLoading(true); setOwnResult(null);
    try {
      const res = await fetch(`${API}/client/${token}/domain`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ custom_domain: ownDomain })
      });
      const data = await res.json();
      // Also deploy to CF Pages with the custom domain
      let cfData = null;
      try {
        const cfRes = await fetch(`${API}/client/${token}/deploy`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ custom_domain: ownDomain }),
        });
        cfData = await cfRes.json();
        if (cfData?.cf_url || cfData?.project) {
          setCfStatus(s => ({ ...s, cf_url: cfData.cf_url, project: cfData.project }));
        }
      } catch (cfErr) { console.warn("[Domain] CF deploy failed (non-critical):", cfErr); }
      setOwnResult({ ...data, cf_project: cfData?.project });
      toast.success("Dominio guardado. Configurá el DNS como se indica.");
    } catch { toast.error("Error guardando dominio"); } finally { setOwnLoading(false); }
  };

  const verify = async () => {
    setVerifyLoading(true);
    try {
      const d = await fetch(`${API}/client/${token}/domain/verify`).then(r => r.json());
      setVerifyResult(d);
      if (d.verified) { toast.success("¡DNS verificado y activo!"); onRefresh(); }
      else toast.info("DNS todavía propagando. Intentá en unos minutos.");
    } catch { toast.error("Error"); } finally { setVerifyLoading(false); }
  };

  const search = async () => {
    if (!searchKw.trim()) return;
    setSearchLoading(true); setSearchResults([]); setSelectedDomain(null);
    try {
      const d = await fetch(`${API}/client/${token}/domain/search`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keyword: searchKw })
      }).then(r => r.json());
      setSearchResults(d.results || []);
      if (d.warning) {
        toast.warning(d.warning, { duration: 8000 });
      } else if ((d.results || []).length === 0) {
        toast.info("No se encontraron resultados. Intentá con otro término.");
      }
    } catch { toast.error("Error buscando dominios"); } finally { setSearchLoading(false); }
  };

  const purchase = async () => {
    if (!selectedDomain) return;
    setPurchaseLoading(true); setPurchaseResult(null);
    try {
      // Use Stripe checkout flow — pay first, then Namecheap registers automatically
      const d = await fetch(`${API}/client/${token}/domain/checkout`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domain_name: selectedDomain, years: 1, contact,
          origin_url: window.location.origin,
        })
      }).then(r => r.json());
      if (d.checkout_url) {
        window.location.href = d.checkout_url;
      } else {
        toast.error(d.detail || "Error al crear sesión de pago");
      }
    } catch { toast.error("Error"); } finally { setPurchaseLoading(false); }
  };

  const redeemFree = async () => {
    setFreeLoading(true);
    try {
      const d = await fetch(`${API}/client/${token}/domain/redeem-free`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain_name: freeForm.domain_name, contact: freeForm })
      }).then(r => r.json());
      if (d.success) { toast.success("¡Solicitud enviada!"); onRefresh(); }
      else toast.error(d.error || "Error");
    } catch { toast.error("Error"); } finally { setFreeLoading(false); }
  };

  const triggerCfDeploy = async () => {
    setCfDomainLoading(true);
    try {
      const res = await fetch(`${API}/client/${token}/deploy`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ custom_domain: ownDomain || null }),
      });
      const d = await res.json();
      if (d.cf_url || d.project) {
        setCfStatus(s => ({ ...s, cf_url: d.cf_url, project: d.project, deployed: true }));
        toast.success("¡Sitio desplegado en Cloudflare Pages!");
      } else {
        toast.error("No se pudo desplegar. Intentá de nuevo.");
      }
    } catch { toast.error("Error de conexión"); } finally { setCfDomainLoading(false); }
  };

  const previewUrl = domainData?.preview_url || "";
  const cfProject = cfStatus?.project || cfStatus?.cf_project;
  const cfUrl = cfStatus?.cf_url;
  const cnameTarget = cfProject ? `${cfProject}.pages.dev` : (domainData?.cname_target || "sitiosya.com.uy");

  return (
    <div className="space-y-3 px-4 pt-4 pb-6">

      {/* ── Cloudflare Pages Status ── */}
      <div className={`rounded-2xl border p-4 ${cfUrl ? "bg-green-50 border-green-200" : "bg-[#F8F4FF] border-[#6D28D9]/20"}`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${cfUrl ? "bg-green-100" : "bg-[#6D28D9]/10"}`}>
            <Globe size={17} className={cfUrl ? "text-green-600" : "text-[#6D28D9]"} />
          </div>
          <div>
            <p className="text-sm font-black text-gray-800">Cloudflare Pages</p>
            <p className="text-[11px] text-gray-500">{cfUrl ? "Sitio activo en la CDN global" : "Desplegá tu sitio para máxima velocidad"}</p>
          </div>
          {cfUrl && (
            <a href={cfUrl} target="_blank" rel="noopener noreferrer" data-testid="cf-url-link"
              className="ml-auto flex items-center gap-1 text-[11px] text-green-700 font-bold hover:underline">
              <ExternalLink size={11} /> Ver sitio
            </a>
          )}
        </div>

        {cfUrl ? (
          <div className="space-y-2">
            <CopyField label="URL en Cloudflare" value={cfUrl} />
            {cfProject && <CopyField label="CNAME (para dominio propio)" value={`${cfProject}.pages.dev`} />}
          </div>
        ) : (
          <button onClick={triggerCfDeploy} disabled={cfDomainLoading} data-testid="deploy-to-cf-btn"
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#6D28D9] text-white rounded-xl text-sm font-bold hover:bg-purple-800 disabled:opacity-60">
            {cfDomainLoading ? <><Loader2 size={13} className="animate-spin" /> Desplegando...</> : <><Zap size={13} /> Desplegar en Cloudflare Pages</>}
          </button>
        )}
      </div>

      {/* ── Custom domain status ── */}
      {domainData?.custom_domain && (
        <div className={`flex items-center gap-3 px-4 py-3.5 rounded-2xl border ${domainData.domain_verified ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${domainData.domain_verified ? "bg-green-100" : "bg-amber-100"}`}>
            {domainData.domain_verified ? <Check size={18} className="text-green-600" /> : <Clock size={18} className="text-amber-600" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-800 truncate">{domainData.custom_domain}</p>
            <p className={`text-xs font-medium mt-0.5 ${domainData.domain_verified ? "text-green-600" : "text-amber-600"}`}>
              {domainData.domain_verified ? "✓ Activo y verificado" : "DNS pendiente de propagación"}
            </p>
          </div>
          {!domainData.domain_verified && (
            <button onClick={verify} disabled={verifyLoading} data-testid="verify-dns-top-btn"
              className="flex-shrink-0 text-xs font-bold text-white bg-amber-500 hover:bg-amber-600 px-3 py-1.5 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
              {verifyLoading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
              Verificar
            </button>
          )}
        </div>
      )}

      {verifyResult && (
        <div className={`text-xs px-4 py-3 rounded-xl border flex items-center gap-2 ${verifyResult.verified ? "bg-green-50 border-green-200 text-green-700" : "bg-gray-50 border-gray-200 text-gray-600"}`}>
          {verifyResult.verified ? <Check size={14} className="text-green-600" /> : <AlertCircle size={14} className="text-gray-400" />}
          {verifyResult.message}
        </div>
      )}

      {/* ── Option 1: Own domain ── */}
      <DomainAccordion id="own" openId={open} onToggle={id => setOpen(open === id ? null : id)} icon={Globe} title="Vincular mi dominio">
        <div className="space-y-4">
          <p className="text-xs text-gray-500 leading-relaxed">
            Ingresá tu dominio para vincularlo a tu sitio en Cloudflare Pages. Te damos las instrucciones exactas de DNS.
          </p>
          <div className="flex gap-2">
            <input data-testid="own-domain-input" value={ownDomain} onChange={e => setOwnDomain(e.target.value)}
              onKeyDown={e => e.key === "Enter" && setDomainAction()}
              placeholder="miempresa.com"
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#0074b7]" />
            <button onClick={setDomainAction} disabled={ownLoading || !ownDomain.trim()} data-testid="save-domain-btn"
              className="px-4 py-2.5 bg-[#0074b7] text-white rounded-xl text-sm font-bold disabled:opacity-50 hover:bg-blue-700">
              {ownLoading ? <Loader2 size={14} className="animate-spin" /> : "Vincular"}
            </button>
          </div>

          {(ownResult || domainData?.custom_domain) && (
            <div className="space-y-3">
              <div className="bg-[#0074b7]/5 border border-[#0074b7]/20 rounded-xl p-4 space-y-3">
                <p className="text-xs font-black text-[#0074b7] uppercase tracking-wider">Instrucciones DNS</p>

                {/* CF CNAME (recommended when CF is deployed) */}
                {cnameTarget.includes(".pages.dev") && (
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-gray-700">Opción A — Cloudflare Pages (recomendado)</p>
                    <p className="text-xs text-gray-500 mb-2">Agregá un registro CNAME en tu proveedor de dominio:</p>
                    <div className="bg-white border border-gray-200 rounded-xl p-3 space-y-2 font-mono text-xs">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 w-14">Tipo:</span>
                        <span className="font-bold">CNAME</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 w-14">Nombre:</span>
                        <span className="font-bold">www</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 w-14">Valor:</span>
                        <span className="font-bold text-[#0074b7] break-all">{cnameTarget}</span>
                      </div>
                    </div>
                    <CopyField label="CNAME Target" value={cnameTarget} />
                  </div>
                )}

                {/* URL redirect fallback */}
                <div className={`${cnameTarget.includes(".pages.dev") ? "border-t border-[#0074b7]/10 pt-3" : ""} space-y-2`}>
                  <p className="text-xs font-bold text-gray-700">{cnameTarget.includes(".pages.dev") ? "Opción B — URL Redirect" : "Opción A — URL Redirect (recomendado)"}</p>
                  <p className="text-xs text-gray-500 mb-2">En tu proveedor, configurá "URL Forward/Redirect" de <code className="bg-gray-100 px-1 rounded">@</code> hacia:</p>
                  {previewUrl && <CopyField label="URL de destino" value={previewUrl} />}
                </div>
              </div>

              <button onClick={verify} disabled={verifyLoading} data-testid="verify-dns-btn"
                className="w-full py-2.5 bg-[#0074b7] text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-blue-700 disabled:opacity-50">
                {verifyLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Verificar DNS ahora
              </button>
            </div>
          )}
        </div>
      </DomainAccordion>

      {/* ── Option 2: Buy domain ── */}
      <DomainAccordion id="buy" openId={open} onToggle={id => setOpen(open === id ? null : id)} icon={ShoppingCart} title="Comprar un dominio nuevo">
        <div className="space-y-4">
          <p className="text-xs text-gray-500">Registramos el dominio y configuramos el DNS automáticamente.</p>

          <div className="flex gap-2">
            <input data-testid="domain-search-input" value={searchKw}
              onChange={e => setSearchKw(e.target.value)}
              onKeyDown={e => e.key === "Enter" && search()}
              placeholder="miempresa (sin extensión)"
              className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#0074b7]" />
            <button onClick={search} disabled={searchLoading} data-testid="domain-search-btn"
              className="px-4 py-2.5 bg-[#0A0A0A] text-white rounded-xl text-sm font-bold disabled:opacity-50 hover:bg-gray-800">
              {searchLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            </button>
          </div>

          {searchResults.length > 0 && (
            <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
              {searchResults.map(r => (
                <button key={r.domain} onClick={() => setSelectedDomain(r.available ? r.domain : selectedDomain)}
                  disabled={!r.available}
                  className={`w-full flex items-center justify-between px-3 py-3 rounded-xl border text-sm transition-all ${
                    !r.available ? "opacity-40 cursor-not-allowed border-gray-100 bg-gray-50" :
                    selectedDomain === r.domain ? "bg-[#0074b7] border-[#0074b7] text-white shadow-md" :
                    "border-gray-200 hover:border-[#0074b7] hover:bg-[#0074b7]/5"}`}>
                  <div className="flex items-center gap-2">
                    {r.available && selectedDomain === r.domain && <Check size={14} />}
                    <span className="font-semibold">{r.domain}</span>
                    {r.premium && <span className="text-[9px] bg-yellow-100 text-yellow-700 font-bold px-1.5 py-0.5 rounded">PREMIUM</span>}
                  </div>
                  <span className={`text-[11px] font-bold ${!r.available ? "text-gray-400" : selectedDomain === r.domain ? "text-white/80" : "text-green-600"}`}>
                    {r.available ? `$${r.price_usd?.toFixed(2)} USD/año` : "No disponible"}
                  </span>
                </button>
              ))}
            </div>
          )}

          {selectedDomain && !purchaseResult?.success && (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Globe size={14} className="text-[#0074b7]" />
                <p className="text-xs font-bold text-gray-800">Datos de contacto para <span className="text-[#0074b7]">{selectedDomain}</span></p>
              </div>
              <DomainContactForm state={contact} onChange={setContact} hasPreFilled={!!(domainData?.contact_info?.first_name || domainData?.contact_info?.email)} />
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl p-3">
                <Zap size={13} className="text-[#0074b7] mt-0.5 flex-shrink-0" />
                <p className="text-[11px] text-blue-700">El DNS se configura <strong>automáticamente</strong> al registrar. Tu sitio queda activo en 15-60 minutos.</p>
              </div>
              <button onClick={purchase} disabled={purchaseLoading} data-testid="purchase-domain-btn"
                className="w-full py-3 bg-[#0074b7] text-white rounded-xl text-sm font-black flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-blue-700">
                {purchaseLoading
                  ? <><Loader2 size={14} className="animate-spin" /> Preparando pago...</>
                  : <><CreditCard size={14} /> Pagar y registrar {selectedDomain}</>}
              </button>
              <p className="text-[10px] text-gray-400 text-center">Pagarás con tarjeta vía Stripe · Seguro y encriptado</p>
            </div>
          )}

          {purchaseResult?.success && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Check size={16} className="text-green-600" />
                <p className="text-sm font-bold text-green-800">¡{purchaseResult.domain} registrado!</p>
              </div>
              {purchaseResult.dns_configured && (
                <div className="flex items-center gap-2 text-xs text-green-700">
                  <Zap size={12} className="text-green-600" />
                  DNS configurado automáticamente vía Namecheap
                </div>
              )}
              {purchaseResult.redirect_url && <CopyField label="URL de tu sitio" value={purchaseResult.redirect_url} />}
              <p className="text-[11px] text-green-600">La propagación puede tardar 15–60 minutos. Usá el botón "Verificar" para confirmar.</p>
            </div>
          )}
        </div>
      </DomainAccordion>

      {/* ── Option 3: Free domain ── */}
      <DomainAccordion id="free" openId={open} onToggle={id => setOpen(open === id ? null : id)} icon={Gift} title="Canjear dominio gratis"
        badge={domainData?.free_domain_eligible && !domainData?.free_domain_redeemed ? "GRATIS" : null}
        badgeColor="bg-green-500">
        {domainData?.free_domain_redeemed ? (
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
            <Check size={18} className="text-green-600 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-green-800">Dominio gratis ya canjeado</p>
              {domainData?.free_domain_request?.domain_name && (
                <p className="text-xs text-green-700 mt-0.5 font-mono">{domainData.free_domain_request.domain_name}</p>
              )}
              <p className="text-[11px] text-green-600 mt-1">Estado: {domainData.free_domain_request?.status || "procesando"}</p>
            </div>
          </div>
        ) : !domainData?.free_domain_eligible ? (
          <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-xl p-4">
            <Lock size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-bold text-gray-700">Beneficio no disponible</p>
              <p className="text-xs text-gray-500 mt-1">El dominio gratis está disponible en planes especiales. Contactá a soporte para más info.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl p-3">
              <Gift size={14} className="text-green-600" />
              <p className="text-xs font-bold text-green-700">Tenés 1 dominio gratis disponible</p>
            </div>
            <input value={freeForm.domain_name} onChange={e => setFreeForm(f => ({ ...f, domain_name: e.target.value }))}
              placeholder="miempresa.com"
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#0074b7]" />
            <DomainContactForm state={freeForm} onChange={setFreeForm} hasPreFilled={!!(domainData?.contact_info?.first_name || domainData?.contact_info?.email)} />
            <button onClick={redeemFree} disabled={freeLoading || !freeForm.domain_name.trim()} data-testid="redeem-domain-btn"
              className="w-full py-3 bg-green-600 text-white rounded-xl text-sm font-black flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-green-700">
              {freeLoading ? <><Loader2 size={14} className="animate-spin" /> Procesando...</> : <><Gift size={14} /> Canjear dominio gratis</>}
            </button>
          </div>
        )}
      </DomainAccordion>

      {/* ── Help ── */}
      <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 flex items-start gap-2.5">
        <HelpCircle size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
        <p className="text-[11px] text-gray-500 leading-relaxed">
          <strong className="text-gray-700">Cloudflare Pages</strong> hace que tu sitio cargue rápido en todo el mundo. Con un dominio propio, configurás un CNAME para que <span className="font-mono text-gray-700">tudominio.com</span> apunte directo a tu sitio.
        </p>
      </div>
    </div>
  );
}

// ─── Scripts / Widgets Tab ────────────────────────────────────────────────────
const STATUS_COLORS = {
  approved: "bg-green-50 border-green-200 text-green-700",
  rejected: "bg-red-50 border-red-200 text-red-700",
  pending_review: "bg-amber-50 border-amber-200 text-amber-700",
};
const STATUS_LABELS = {
  approved: "Aprobado",
  rejected: "Rechazado",
  pending_review: "En revisión",
};

function ScriptsTab({ token }) {
  const [widgets, setWidgets] = useState([]);
  const [form, setForm] = useState({ name: "", code: "", position: "head" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadWidgets = useCallback(async () => {
    try {
      const d = await fetch(`${API}/client/${token}/widgets`).then(r => r.json());
      setWidgets(d.widgets || []);
    } catch (err) { console.warn("[Widgets] Failed to load widgets:", err); }
  }, [token]);

  useEffect(() => { loadWidgets(); }, [loadWidgets]);

  const submit = async () => {
    if (!form.name.trim() || !form.code.trim()) return;
    setLoading(true); setResult(null);
    try {
      const d = await fetch(`${API}/client/${token}/widgets`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      }).then(r => r.json());
      setResult(d);
      if (d.status !== "rejected") {
        setForm({ name: "", code: "", position: "head" });
        setShowForm(false);
        loadWidgets();
        toast.success(d.status === "approved" ? "¡Script aprobado e inyectado!" : "Enviado para revisión.");
      }
    } catch { toast.error("Error al enviar"); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    try {
      await fetch(`${API}/client/${token}/widgets/${id}`, { method: "DELETE" });
      loadWidgets();
      toast.success("Script eliminado.");
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <div className="space-y-4 px-4 pt-4 pb-6">
      {/* Info card */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Code2 size={15} className="text-gray-600" />
          <h3 className="text-sm font-black text-[#0A0A0A]">Píxeles y Scripts de Seguimiento</h3>
        </div>
        <p className="text-xs text-gray-500 leading-relaxed">
          Agregá herramientas como Google Analytics, Meta Pixel o Google Tag Manager. Los scripts son analizados automáticamente por seguridad.
        </p>
      </div>

      {/* Add button */}
      {!showForm && (
        <button onClick={() => setShowForm(true)} data-testid="add-widget-btn"
          className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-2xl text-sm font-semibold text-gray-500 hover:border-[#6D28D9] hover:text-[#6D28D9] transition-colors">
          <Plus size={16} /> Agregar script
        </button>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold">Nuevo script</h3>
            <button onClick={() => { setShowForm(false); setResult(null); }} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
          </div>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Nombre (ej: Google Analytics)" data-testid="widget-name-input"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#6D28D9]" />
          <textarea value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
            placeholder="Pegá el código del script aquí..." rows={5} data-testid="widget-code-input"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#6D28D9] font-mono text-xs resize-none" />
          <select value={form.position} onChange={e => setForm(f => ({ ...f, position: e.target.value }))}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-gray-400">
            <option value="head">En el encabezado (&lt;head&gt;)</option>
            <option value="body">Antes del cierre (&lt;/body&gt;)</option>
          </select>

          {result && (
            <div className={`rounded-xl p-3 border ${STATUS_COLORS[result.status] || "bg-gray-50 border-gray-200"}`}>
              <p className="text-xs font-bold mb-0.5">{STATUS_LABELS[result.status] || result.status}</p>
              {result.tool && <p className="text-xs">Herramienta: <strong>{result.tool}</strong></p>}
              {result.reason && <p className="text-xs mt-0.5">{result.reason}</p>}
            </div>
          )}

          <button onClick={submit} disabled={loading || !form.name.trim() || !form.code.trim()}
            data-testid="submit-widget-btn"
            className="w-full py-2.5 bg-[#6D28D9] text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Analizar y enviar
          </button>
        </div>
      )}

      {/* List */}
      {widgets.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Scripts activos</p>
          {widgets.map(w => (
            <div key={w.id} className="bg-white border border-gray-200 rounded-xl px-3 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#0A0A0A] truncate">{w.name}</p>
                {w.tool && <p className="text-xs text-gray-500">{w.tool}</p>}
              </div>
              <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${STATUS_COLORS[w.status]}`}>
                {STATUS_LABELS[w.status] || w.status}
              </span>
              {w.status !== "pending_review" && (
                <button onClick={() => remove(w.id)} className="text-gray-400 hover:text-red-500 flex-shrink-0">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Subscription Tab ─────────────────────────────────────────────────────────
function SubscriptionTab({ token, account, settings, setActiveTab }) {
  const [loading, setLoading] = useState(false);
  const secsLeft = useCountdown(account?.offer_expires_at);
  const offerActive = secsLeft > 0 && !!account?.offer_expires_at;
  const hours = Math.floor(secsLeft / 3600);
  const mins = Math.floor((secsLeft % 3600) / 60);
  const secs = secsLeft % 60;
  const openPortal = async () => {
    setLoading(true);
    try {
      const d = await fetch(`${API}/client/${token}/subscription/portal?return_url=${encodeURIComponent(window.location.href)}`).then(r => r.json());
      if (d.portal_url) window.location.href = d.portal_url;
      else toast.info(d.message || "No se encontró suscripción activa.");
    } catch { toast.error("Error."); } finally { setLoading(false); }
  };

  // Determine price to display
  const subAmount = account?.subscription_amount;
  const subCurrency = account?.subscription_currency || settings?.offer_currency || "UYU";
  const offerPrice = settings?.offer_price;
  const displayPrice = subAmount ?? offerPrice;
  const priceLabel = displayPrice
    ? `${subCurrency} $${Number(displayPrice).toLocaleString("es-UY")}/mes`
    : null;

  const waSupport = settings?.whatsapp_number || "";
  const waMsg = encodeURIComponent("Hola, soy cliente de SitiosYA y necesito ayuda con mi plan.");
  const waUrl = waSupport ? `https://wa.me/${waSupport.replace(/\D/g, "")}?text=${waMsg}` : null;

  // Determine real plan state — never lie to the user about payment status.
  const isPaid    = !!account?.is_paid;
  const isTrial   = !isPaid && !!account?.is_trial;
  const planLabel = isPaid ? "Sitio Web Activo" : (isTrial ? "Prueba gratuita en curso" : "Pendiente de pago");
  const planColor = isPaid ? "green" : (isTrial ? "amber" : "red");
  const statusDotBg = { green: "bg-green-500", amber: "bg-amber-500", red: "bg-red-500" }[planColor];
  const statusText  = { green: "text-green-600", amber: "text-amber-600", red: "text-red-600" }[planColor];
  const statusLabel = isPaid ? "Activo" : (isTrial ? "En prueba" : "Sin activar");

  return (
    <div className="space-y-4 px-4 pt-4 pb-6">
      <div className="bg-white border border-gray-200 rounded-2xl p-4">
        <div className="flex items-start justify-between">
          <div>
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">Plan actual</span>
            <h3 className="text-lg font-black text-[#0A0A0A] mt-1" data-testid="plan-status-title">{planLabel}</h3>
            <div className="flex items-center gap-1.5 mt-1">
              <span className={`w-2 h-2 rounded-full ${statusDotBg}`} />
              <span className={`text-xs font-semibold ${statusText}`} data-testid="plan-status-badge">{statusLabel}</span>
            </div>
          </div>
          <div className="w-12 h-12 bg-[#0A0A0A] rounded-xl flex items-center justify-center">
            <CreditCard size={22} className="text-white" />
          </div>
        </div>
        {!isPaid && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <p className="text-[11px] text-red-700 leading-relaxed">
              <strong>Tu sitio aún no está publicado.</strong> Para activarlo, completá el pago desde el botón
              <span className="font-bold"> "Comprar y Publicar"</span> del editor.
            </p>
          </div>
        )}

        {/* Urgency countdown — same info as the top banner, visible in the Plan tab too */}
        {!isPaid && offerActive && (
          <div
            data-testid="plan-countdown-strip"
            className="mt-3 rounded-xl px-4 py-3 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #FEF3C7 0%, #FED7AA 100%)",
              border: "1px solid #FB923C",
            }}
          >
            <div className="flex items-start gap-2 relative z-10">
              <Timer size={16} className="text-[#B45309] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-wider text-[#B45309]">
                  Oferta por tiempo limitado
                </p>
                <p className="text-[12px] font-bold text-[#7C2D12] mt-0.5 leading-tight">
                  Tu sitio y esta oferta serán eliminados en
                </p>
                <div className="flex items-baseline gap-1 mt-1.5">
                  {[
                    { v: hours, l: "h" },
                    { v: mins,  l: "m" },
                    { v: secs,  l: "s" },
                  ].map(({ v, l }, i) => (
                    <React.Fragment key={l}>
                      <span className="text-[20px] font-black text-[#7C2D12] tabular-nums leading-none">
                        {String(v).padStart(2, "0")}
                      </span>
                      <span className="text-[10px] font-bold text-[#B45309]">{l}</span>
                      {i < 2 && <span className="text-[14px] text-[#FB923C] mx-0.5">:</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        {priceLabel && (
          <div className="mt-3 bg-[#0A0A0A] rounded-xl px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-gray-400">{isPaid ? "Monto mensual" : "Precio al activar"}</span>
            <span className="text-sm font-black text-[#ffc700]">{priceLabel}</span>
          </div>
        )}
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
          {[["Sitio web", isPaid ? "Publicado" : "En preview"],["Modificaciones gratuitas","5 por mes"],["Soporte","WhatsApp / Email"]].map(([l,v]) => (
            <div key={l} className="flex justify-between">
              <span className="text-xs text-gray-400">{l}</span>
              <span className="text-xs font-semibold text-[#0A0A0A]">{v}</span>
            </div>
          ))}
        </div>
      </div>
      {isPaid ? (
        <button data-testid="manage-subscription-btn" onClick={openPortal} disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#0074b7] text-white rounded-2xl font-bold text-sm hover:bg-blue-700 disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />}
          {loading ? "Cargando portal..." : "Gestionar suscripción en Stripe"}
        </button>
      ) : (
        <button data-testid="activate-plan-btn" onClick={() => setActiveTab?.("edit")}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#ffc700] text-black rounded-2xl font-black text-sm hover:bg-yellow-400 transition-colors">
          <CreditCard size={16} /> Activar mi plan ahora
        </button>
      )}
      {waUrl && (
        <a href={waUrl} target="_blank" rel="noopener noreferrer"
          data-testid="wa-support-plan-btn"
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-[#25D366] text-white rounded-2xl font-bold text-sm hover:bg-green-600 transition-colors">
          <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white flex-shrink-0">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          Soporte por WhatsApp
        </a>
      )}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle size={15} className="text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-bold text-amber-800">¿Querés cancelar?</p>
            <p className="text-xs text-amber-700 mt-0.5">Desde el portal de Stripe podés gestionar o cancelar. Al cancelar, el sitio queda activo hasta el fin del período pagado.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── WhatsApp FAB ─────────────────────────────────────────────────────────────
function WhatsAppFAB({ business, waNumber }) {
  if (!waNumber) return null;
  const msg = encodeURIComponent(
    `Hola, soy ${business?.name || "cliente"} y estoy accediendo desde mi portal de cliente. Tengo una consulta sobre mi sitio web.`
  );
  const url = `https://wa.me/${waNumber.replace(/\D/g, "")}?text=${msg}`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="wa-fab"
      className="fixed bottom-6 right-4 z-50 flex flex-col items-center gap-1">
      <span className="bg-[#0A0A0A] text-white text-[10px] font-bold px-2.5 py-1 rounded-full shadow-md whitespace-nowrap">
        ¿Consultas?
      </span>
      <div className="w-14 h-14 bg-[#25D366] rounded-full shadow-xl flex items-center justify-center">
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      </div>
    </a>
  );
}

// ─── Referral Tab ─────────────────────────────────────────────────────────────
function ReferralTab({ token }) {
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetch(`${API}/client/${token}/referral`).then(r => r.json());
      setReferral(data);
    } catch (err) { console.warn("[Referral] Load failed:", err); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const generateCode = async () => {
    setGenerating(true);
    try {
      const data = await fetch(`${API}/client/${token}/referral/generate`, { method: "POST" }).then(r => r.json());
      setReferral(data);
      toast.success("¡Tu link de referido fue creado!");
    } catch (err) {
      console.error("[Referral] Link generation failed:", err);
      toast.error("Error al generar link");
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = () => {
    if (!referral?.referral_url) return;
    navigator.clipboard.writeText(referral.referral_url).then(() => {
      setCopied(true);
      toast.success("¡Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-[#ffc700]" /></div>;
  }

  const count = referral?.referral_count || 0;
  const credits = referral?.referral_credits || 0;
  const hasCode = referral?.has_code;
  const commission = referral?.commission_per_referral || 0;
  const currency = referral?.currency || "UYU";
  const commissionLabel = commission
    ? `${currency} $${Number(commission).toLocaleString("es-UY")}`
    : "$0";

  return (
    <div className="min-h-[60vh]" style={{ background: "#0D0D0D" }}>
      {/* ── Hero ── */}
      <div className="relative overflow-hidden px-4 pt-8 pb-6">
        {/* Background glow */}
        <div style={{
          position: "absolute", top: "-60px", left: "50%", transform: "translateX(-50%)",
          width: 340, height: 340, borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,199,0,0.10) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
        <div className="relative text-center max-w-md mx-auto">
          {/* Badge */}
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(255,199,0,0.10)", border: "1px solid rgba(255,199,0,0.25)",
            borderRadius: 99, padding: "4px 14px", marginBottom: 16,
          }}>
            <Zap size={11} style={{ color: "#ffc700" }} />
            <span style={{ color: "#ffc700", fontSize: 10, fontWeight: 900, letterSpacing: "0.12em", textTransform: "uppercase" }}>
              Programa de referidos
            </span>
          </div>
          {/* Headline */}
          <h1 style={{
            fontSize: "clamp(1.6rem, 6vw, 2.4rem)", fontWeight: 900,
            lineHeight: 1.05, color: "#fff", marginBottom: 10, letterSpacing: "-0.02em",
          }}>
            Referí.<br />
            <span style={{ WebkitTextStroke: "1.5px #ffc700", color: "transparent" }}>Ganá {commissionLabel}.</span><br />
            Repetí.
          </h1>
          <p style={{ color: "#999", fontSize: 13, lineHeight: 1.6, maxWidth: 300, margin: "0 auto 24px" }}>
            Cada persona que contrate SitiosYA con tu link te da <strong style={{ color: "#ffc700" }}>3 meses gratis</strong> de plataforma. Sin límite.
          </p>

          {/* Stats row — always visible */}
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 24 }}>
            {[
              { value: `${count}`, label: "Referidos", icon: <Users size={13} style={{ color: "#ffc700" }} /> },
              { value: `$${credits}`, label: "Créditos", icon: <DollarSign size={13} style={{ color: "#ffc700" }} /> },
              { value: "∞", label: "Sin límite", icon: <Zap size={13} style={{ color: "#ffc700" }} /> },
            ].map((s) => (
              <div key={s.label} style={{
                flex: 1, background: "#111", border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 14, padding: "12px 8px", textAlign: "center",
              }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 4 }}>{s.icon}</div>
                <div style={{ fontSize: s.label === "Créditos" && credits > 0 ? 20 : 18, fontWeight: 900, color: s.label === "Créditos" ? "#ffc700" : "#fff", lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "#555", marginTop: 3, fontWeight: 600 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Link Section ── */}
      <div className="px-4 max-w-md mx-auto space-y-3">
        {hasCode ? (
          <div style={{
            background: "linear-gradient(135deg, rgba(255,199,0,0.08) 0%, #111 100%)",
            border: "1px solid rgba(255,199,0,0.20)", borderRadius: 18, padding: 20,
          }}>
            <p style={{ fontSize: 10, fontWeight: 900, color: "#ffc700", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 10 }}>
              Tu link único
            </p>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
              <div style={{
                flex: 1, background: "rgba(0,0,0,0.4)", border: "1px solid rgba(255,199,0,0.15)",
                borderRadius: 10, padding: "10px 14px", overflow: "hidden",
              }}>
                <p style={{ fontSize: 11, color: "#ffc700", fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", margin: 0 }}>
                  {referral?.referral_url}
                </p>
              </div>
              <button data-testid="copy-referral-btn" onClick={copyLink} style={{
                background: copied ? "#22c55e" : "#ffc700", color: copied ? "#fff" : "#000",
                border: "none", borderRadius: 10, padding: "10px 16px",
                fontWeight: 900, fontSize: 12, cursor: "pointer",
                display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
                transition: "all 0.2s",
              }}>
                {copied ? <Check size={15} /> : <Copy size={15} />}
                {copied ? "Copiado" : "Copiar"}
              </button>
            </div>
            {/* Tip */}
            <div style={{
              background: "rgba(255,199,0,0.06)", border: "1px solid rgba(255,199,0,0.10)",
              borderRadius: 10, padding: "10px 14px",
              display: "flex", gap: 10, alignItems: "flex-start",
            }}>
              <Sparkles size={13} style={{ color: "#ffc700", flexShrink: 0, marginTop: 1 }} />
              <p style={{ fontSize: 11, color: "#888", margin: 0, lineHeight: 1.5 }}>
                Compartí tu link por WhatsApp, Instagram o email. Cuantos más contraten, más créditos ganás.
              </p>
            </div>
          </div>
        ) : (
          <div style={{
            border: "2px dashed rgba(255,199,0,0.25)", borderRadius: 18,
            padding: 24, textAlign: "center",
          }}>
            <div style={{
              width: 52, height: 52, background: "rgba(255,199,0,0.10)",
              border: "1px solid rgba(255,199,0,0.20)", borderRadius: 14,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 14px",
            }}>
              <Gift size={22} style={{ color: "#ffc700" }} />
            </div>
            <p style={{ color: "#fff", fontWeight: 800, fontSize: 15, marginBottom: 6 }}>
              Generá tu link y empezá a ganar
            </p>
            <p style={{ color: "#666", fontSize: 12, marginBottom: 18, lineHeight: 1.5 }}>
              Tu link único es gratuito y funciona para siempre.
            </p>
            <button data-testid="generate-referral-btn" onClick={generateCode} disabled={generating} style={{
              background: "linear-gradient(135deg, #ffc700, #ff9500)",
              color: "#000", border: "none", borderRadius: 12,
              padding: "13px 28px", fontWeight: 900, fontSize: 13,
              cursor: generating ? "not-allowed" : "pointer",
              opacity: generating ? 0.7 : 1,
              display: "flex", alignItems: "center", gap: 8, margin: "0 auto",
              boxShadow: "0 4px 20px rgba(255,199,0,0.25)",
            }}>
              {generating ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : <Zap size={16} />}
              {generating ? "Generando..." : "Crear mi link gratuito"}
            </button>
          </div>
        )}

        {/* ── How it works ── */}
        <div style={{ background: "#111", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 18, padding: 20 }}>
          <p style={{ fontSize: 10, fontWeight: 900, color: "#555", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 16 }}>
            Cómo funciona
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {[
              { n: "01", title: "Compartís tu link", desc: "Envialo por WhatsApp, email, redes. Donde quieras.", color: "#ffc700" },
              { n: "02", title: "Alguien contrata", desc: "Esa persona entra a SitiosYA y compra su plan.", color: "#0074b7" },
              { n: "03", title: `Ganás ${commissionLabel}`, desc: "Se acreditan automáticamente — son 3 meses de servicio gratis.", color: "#22c55e" },
            ].map((step) => (
              <div key={step.n} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{
                  width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                  background: `${step.color}15`, border: `1px solid ${step.color}30`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <span style={{ color: step.color, fontWeight: 900, fontSize: 11, fontFamily: "monospace" }}>{step.n}</span>
                </div>
                <div>
                  <p style={{ color: "#fff", fontWeight: 700, fontSize: 13, margin: "0 0 3px" }}>{step.title}</p>
                  <p style={{ color: "#666", fontSize: 11.5, margin: 0, lineHeight: 1.5 }}>{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Credits info ── */}
        <div style={{
          background: "rgba(0,116,183,0.06)", border: "1px solid rgba(0,116,183,0.15)",
          borderRadius: 14, padding: "14px 16px",
          display: "flex", gap: 12, alignItems: "flex-start",
        }}>
          <HelpCircle size={14} style={{ color: "#0074b7", flexShrink: 0, marginTop: 1 }} />
          <p style={{ color: "#666", fontSize: 11, margin: 0, lineHeight: 1.6 }}>
            Los créditos se usan para pagar meses de servicio, paquetes de edición IA extra, o cualquier mejora futura. No vencen nunca.
          </p>
        </div>

        <div style={{ height: 24 }} />
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function ClientDashboard() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mods, setMods] = useState(null);
  const [domainData, setDomainData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState(() => searchParams.get("tab") || "edit");

  const loadData = useCallback(async () => {
    try {
      const [main, modsR, domainR] = await Promise.all([
        fetch(`${API}/client/${token}`).then(r => r.json()),
        fetch(`${API}/client/${token}/mods/status`).then(r => r.json()),
        fetch(`${API}/client/${token}/domain`).then(r => r.json()),
      ]);
      if (main.detail) setError(true);
      else {
        setData(main);
        setMods(modsR);
        setDomainData(domainR);
        // public_settings comes embedded in the main response (no auth needed)
        setSettings(main.public_settings || {});
      }
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  // Handle return from mod pack payment
  useEffect(() => {
    const modSession = searchParams.get("mod_session");
    if (!modSession) return;
    fetch(`${API}/client/${token}/mods/verify-payment`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: modSession }),
    }).then(r => r.json()).then(d => {
      if (d.success && !d.already_processed) toast.success(`¡${d.mods_added} modificaciones agregadas!`);
      loadData();
    }).catch(() => {});
  }, [searchParams, token, loadData]);

  // Handle return from monthly subscription payment
  useEffect(() => {
    const subSession = searchParams.get("sub_session");
    if (!subSession) return;
    fetch(`${API}/client/${token}/subscribe/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: subSession }),
    }).then(r => r.json()).then(d => {
      if (d.success && !d.already_processed) {
        toast.success(`¡Suscripción activada! Tenés ${d.mods_added} modificaciones disponibles.`);
      } else if (d.already_processed) {
        toast.info("Suscripción ya confirmada.");
      }
      loadData();
    }).catch(() => {});
  }, [searchParams, token, loadData]);

  // Handle return from upgrade payment
  useEffect(() => {
    const upgradeSession = searchParams.get("upgrade_session");
    const instruction = searchParams.get("upgrade_instruction");
    if (!upgradeSession) return;
    fetch(`${API}/client/${token}/chat/upgrade/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: upgradeSession, instruction: instruction || "" }),
    }).then(r => r.json()).then(d => {
      if (d.success) toast.success("¡Pago confirmado! Los cambios están listos para revisar.");
      else if (d.already_processed) {}
      loadData();
    }).catch(() => {});
  }, [searchParams, token, loadData]);

  // Iter91.3 — Handle return from big-change quote payment.
  // After verify, redirect to the editor where the AI chat will pick up
  // the paid_quote_id from sessionStorage and finish the work.
  useEffect(() => {
    const bigChangeSession = searchParams.get("big_change_session");
    if (!bigChangeSession) return;
    // The Stripe metadata.quote_id comes back via the session lookup
    fetch(`${API}/client/${token}/agent/v2/big-change/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        // We don't have quote_id directly in the URL — try to derive it from
        // the most recent unconsumed quote for this token. Backend looks both up.
        quote_id: searchParams.get("quote_id") || "",
        session_id: bigChangeSession,
      }),
    }).then(r => r.json()).then(d => {
      if (d.paid || d.already_paid) {
        const qid = d.quote_id;
        const instr = d.instruction || "";
        if (qid && instr) {
          // Stash for the editor's AI chat to consume on the next call.
          try {
            sessionStorage.setItem("sy_paid_quote_id", qid);
            sessionStorage.setItem("sy_paid_quote_instruction", instr);
          } catch {}
          toast.success("¡Pago confirmado! Volviendo al editor para terminar el cambio…");
          // Redirect to the editor — the chat will auto-resume with paid_quote_id.
          setTimeout(() => navigate(`/editor/${token}?resume_quote=${qid}`), 800);
        } else {
          toast.success("¡Pago confirmado!");
          loadData();
        }
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token]);

  // Handle return from domain purchase payment
  useEffect(() => {
    const domainSession = searchParams.get("domain_session");
    if (!domainSession) return;
    fetch(`${API}/client/${token}/domain/verify-payment`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: domainSession }),
    }).then(r => r.json()).then(d => {
      if (d.status === "registered" && !d.already_done) {
        toast.success(`¡Dominio ${d.domain} registrado correctamente!`);
      } else if (d.already_done) {
        toast.info("El dominio ya estaba registrado.");
      } else if (d.status === "error") {
        toast.error(`Error al registrar dominio: ${d.error || "Contactá soporte"}`);
      }
      loadData();
    }).catch(() => {});
  }, [searchParams, token, loadData]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Loader2 className="animate-spin text-gray-400" size={36} />
    </div>
  );
  if (error) return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-6">
      <div className="text-center">
        <AlertCircle size={40} className="text-gray-300 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-gray-700">Acceso no válido</h2>
        <p className="text-gray-500 text-sm mt-1">El enlace no es válido o ha expirado.</p>
      </div>
    </div>
  );

  const { business, site, account } = data;
  const previewUrl = site?.slug ? `/pv/${site.slug}` : null;
  const waNumber = settings?.whatsapp_number || "";

  const TABS = [
    { id: "edit",      icon: Wand2,      label: "Editar" },
    { id: "analytics", icon: BarChart2,  label: "Stats" },
    { id: "domain",    icon: Globe,      label: "Dominio" },
    { id: "scripts",   icon: Code2,      label: "Píxeles" },
    { id: "referral",  icon: Gift,       label: "Referidos" },
    { id: "sub",       icon: CreditCard, label: "Plan" },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col md:flex-row">
      {/* Client navigation sidebar — vertical on desktop, bottom-bar on mobile */}
      <ClientNavSidebar
        activeTab={tab}
        onNavigate={(t) => setTab(t)}
        previewUrl={previewUrl}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Minimal top header */}
        <div className="flex items-center justify-between px-4 pt-5 pb-4 bg-[#0A0A0A]">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            {/* Mobile-only logo at the top — desktop gets it from the sidebar */}
            <div className="md:hidden w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-[#1a1a1a] border border-white/10 flex items-center justify-center">
              <img src="/logo.png" alt="Sitios YA" className="w-6 h-6 object-contain" />
            </div>
            <div className="w-8 h-8 bg-[#ffc700] rounded-xl flex items-center justify-center flex-shrink-0">
              <Globe size={14} className="text-black" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-black text-white leading-tight truncate max-w-[180px] md:max-w-[260px]">
                {business?.name || "Mi Sitio Web"}
              </p>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                <span className="text-[9px] text-green-400 font-medium">Activo</span>
              </div>
            </div>
          </div>
          {previewUrl && (
            <a data-testid="view-site-btn" href={previewUrl} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.07] border border-white/[0.1] rounded-xl text-[11px] font-semibold text-gray-300 hover:bg-white/[0.12] transition-all flex-shrink-0">
              <ExternalLink size={11} /> <span className="hidden sm:inline">Ver sitio</span>
            </a>
          )}
        </div>

        {/* Tab content — extra bottom padding on mobile so content
            isn't hidden behind the bottom nav bar. */}
        <div className="flex-1 bg-gray-50 md:rounded-tl-[24px] overflow-hidden pb-16 md:pb-0">
          {tab === "edit"      && <Navigate to={`/editor/${token}`} replace />}
          {tab === "analytics" && <SiteAnalyticsTab token={token} />}
          {tab === "domain"    && <DomainTab token={token} domainData={domainData} onRefresh={loadData} />}
          {tab === "scripts"   && <ScriptsTab token={token} />}
          {tab === "referral"  && <ReferralTab token={token} />}
          {tab === "sub"       && <SubscriptionTab token={token} account={account} settings={settings} setActiveTab={setTab} />}
        </div>
      </div>

      <WhatsAppFAB business={business} waNumber={waNumber} />
    </div>
  );
}
